import React, {useState} from "react";
import {
	isAuthorizedByPermission,
	pageAuthorizedByPermission
} from "../../fragments/PageAuthorization";
import {GridColDef} from "@mui/x-data-grid";
import {Link} from "react-router-dom";
import {
	Breadcrumbs,
	Button, Checkbox,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem,
	Paper, Select, Stack, TextField,
	Typography
} from "@mui/material";
import {checkboxColumn, textColumn, tooltipColumn, uuidColumn} from "../../../components/common/grid/Columns";
import {formatDateTimeShort} from "../../../global/GlobalState";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {GraphQlDataGrid} from "../../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../../services/GraphQLServices";
import {translateFunction} from "../../../localization/TranslationService";
import {AuthQueries, POLICIES, POLICIES_INDEX} from "../../../backend/authorization/AuthQueries";
import {linkAuthPolicyDetailsFor, openAuthPolicyDetailsFor} from "./PolicyDetailsPage";
import {AuthPolicy} from "../../../backend/authorization/models/AuthPolicy";
import {permissions} from "../../../global/Permissions";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {useQuery} from "@apollo/client";
import {putPolicy} from "../Functions";

const translate = translateFunction();

export const PoliciesOverviewPage = pageAuthorizedByPermission(function () {
	const columns: GridColDef<AuthPolicy>[] = [
		{
			field: "",
			headerName: translate("Link"),
			renderCell: (params) => (
				<Link to={linkAuthPolicyDetailsFor(params.row.krn)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
			hideable: false,
		},
		{
			field: "krn",
			headerName: translate("Kizi Resource Name"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "id",
			headerName: translate("Id"),
			minWidth: 150,
			renderCell: tooltipColumn(uuidColumn()),
			flex: 1,
		},
		{
			field: "tenant",
			headerName: translate("Tenant"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "name",
			headerName: translate("Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "rules",
			headerName: translate("Rules"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueGetter: (params) => {
				const actions = params.row.rules.map(it => it.actions.length).sum();
				const resources = params.row.rules.map(it => it.resources.length).sum();
				return `${params.row.rules.length} (${actions}:${resources})`;
			},
			sortable: false,
			editable: false,
			filterable: false,
			flex: 1,
		},
		{
			field: "subPolicies",
			headerName: translate("Sub-policies"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueGetter: (params) => params.row.subPolicies.length,
			sortable: false,
			editable: false,
			filterable: false,
			flex: 1,
		},
		{
			field: "isSynchronized",
			headerName: translate("Is Synchronized"),
			minWidth: 150,
			renderCell: checkboxColumn(),
			flex: 1,
		},
		{
			field: "version",
			headerName: translate("Version"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1
		}
	];
	
	const editable = isAuthorizedByPermission(permissions.authorizationPutPolicy);
	
	const existingPoliciesQuery = useQuery<AuthQueries>(POLICIES_INDEX, {
		client: graphQLServices.authorizationClient,
		defaultOptions: {
			fetchPolicy: "no-cache"
		}
	});
	
	if (existingPoliciesQuery.error)
		throw existingPoliciesQuery.error;
	
	const existingPolicies = existingPoliciesQuery.data?.policies.nodes.map(it => it.krn) ?? [];
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}}
				to="/authorization">{translate("Authorization")}</Link>
			<Typography color="text.primary">{translate("Policies")}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<GraphQlDataGrid
				rows={[]}
				query={POLICIES}
				graphFunction={"policies"}
				client={graphQLServices.authorizationClient}
				rowsPerPageOptions={[50, 100, 500, 1000]}
				initialPageSize={100}
				columns={columns}
				getRowId={row => row.krn}
				toolbarElements={[
					<NewPolicyDialog
						key={"createDialog"}
						disabled={!editable}
						existingPolicies={new Set<string>(existingPolicies)}
					/>
				]}
				defaultSortModel={[
					{
						field: "name",
						sort: "asc"
					}
				]}
				initialState={{
					columns: {
						columnVisibilityModel: {
							krn: false,
							id: false,
							version: false,
							creationDateTime: false
						},
					},
				}}
			/>
		</Paper>}
	/>;
}, [permissions.authorizationRead]);

interface NewPolicyDialogProps {
	readonly disabled: boolean;
	readonly existingPolicies: Set<string>;
}

function NewPolicyDialog(props: NewPolicyDialogProps) {
	const [open, setOpen] = useState(false);
	
	const [tenant, setTenant] = useState<string>("kizi");
	const [name, setName] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSynchronized, setIsSynchronized] = useState<boolean>(false);
	
	function handleClickOpen() {
		setOpen(true);
	}
	function handleClose() {
		setOpen(false);
	}
	async function handleCreate() {
		if (props.existingPolicies.has(name)) {
			throw new Error("A policy for this tenant with this name already exists.");
		} else {
			const krn = `krn:${tenant}:auth-policy:${name}`;
			
			await putPolicy(null, {
				id: crypto.randomUUID(),
				krn,
				tenant,
				name,
				rules: [],
				subPolicies: [],
				isSynchronized,
			});
			
			setOpen(false);
			openAuthPolicyDetailsFor(krn);
		}
	}
	
	return <>
		<Button
			variant={"outlined"}
			disabled={props.disabled}
			onClick={handleClickOpen}
		>{translate("Add")}</Button>
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>{translate("New Policy")}</DialogTitle>
			<DialogContent>
				<DialogContentText>
					{translate("Please provide the following information to create a new policy.")}
				</DialogContentText>
				<FormControl margin={"dense"} fullWidth
					error={!!error}>
					<InputLabel id={"tenant-label"}>{translate("Tenant")}</InputLabel>
					<Select
						labelId={"tenant-label"}
						label={translate("Tenant")}
						variant={"standard"}
						value={tenant}
						onChange={event => {
							const value = event.target.value;
							setTenant(value);
							
							const krn = `krn:${value}:auth-policy:${name}`;
							if (props.existingPolicies.has(krn)) {
								setError("A policy for this tenant with this name already exists.");
							} else {
								setError("");
							}
						}}
					>
						<MenuItem key={"kizi"} value={"kizi"}>{"kizi"}</MenuItem>
						<MenuItem key={"risk"} value={"risk"}>{"risk"}</MenuItem>
					</Select>
				</FormControl>
				<TextField
					error={!!error}
					helperText={error ? translate(error) : "​"}
					value={name}
					autoFocus
					margin="dense"
					label={translate("Name")}
					fullWidth
					variant="standard"
					required={true}
					inputProps={{
						pattern: "^[a-zA-Z0-9.$\\[\\]]+$",
					}}
					onChange={event => {
						const value = event.target.value;
						setName(value);
						const krn = `krn:${tenant}:auth-policy:${value}`;
						if (props.existingPolicies.has(krn)) {
							setError("A policy for this tenant with this name already exists.");
						} else {
							setError("");
						}
					}}
				/>
				<FormControlLabel
					label={translate("Is synchronized")}
					control={<Checkbox
						checked={isSynchronized}
						onChange={event => {
							setIsSynchronized(event.target.checked);
						}}
					/>}
				/>
				<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
					<Button onClick={handleClose}>{translate("Cancel")}</Button>
					<ProcessButton
						defaultText={"Create"}
						completedText={"Created"}
						inProgressText={"Creating"}
						onClick={handleCreate}
						buttonProps={{
							disabled: !!error
						}}
					/>
				</Stack>
			</DialogContent>
		</Dialog>
	</>;
}
